import React, { useMemo } from 'react';
import { FileText } from 'lucide-react';
import ReviewHeader, { HeaderAction, FormStatus } from './ReviewHeader';
import ReviewSidebar, { SidebarSection } from './ReviewSidebar';
import SectionCard, { SectionData } from './SectionCard';
import { useFormNavigation } from './useFormNavigation';

export interface ReviewSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  data: SectionData;
}

interface ReviewLayoutProps {
  /** Header title */
  title: string;
  /** Header subtitle */
  subtitle?: string;
  /** Sidebar title */
  sidebarTitle: string;
  /** Sections to display */
  sections: ReviewSection[];
  /** Overall completion percentage */
  completionPercentage: number;
  /** Form status */
  status?: FormStatus;
  /** Last updated timestamp */
  lastUpdated?: string;
  /** Edit callback */
  onEdit?: () => void;
  /** Download PDF callback */
  onDownloadPDF?: () => void;
  /** Additional header actions */
  actions?: HeaderAction[];
  /** Content to render above sections (e.g., version selector) */
  headerContent?: React.ReactNode;
  /** Content to render below sections */
  footerContent?: React.ReactNode;
}

const ReviewLayout: React.FC<ReviewLayoutProps> = ({
  title,
  subtitle,
  sidebarTitle,
  sections,
  completionPercentage,
  status,
  lastUpdated,
  onEdit,
  onDownloadPDF,
  actions,
  headerContent,
  footerContent,
}) => {
  const sectionIds = useMemo(() => sections.map(s => s.id), [sections]);
  const { activeSection, scrollToSection } = useFormNavigation(sectionIds);

  const sidebarSections: SidebarSection[] = useMemo(() =>
    sections.map(s => ({ id: s.id, title: s.title, icon: s.icon })),
    [sections]
  );

  const completionStatus: Record<string, boolean> = useMemo(() => {
    const status: Record<string, boolean> = {};
    sections.forEach(s => {
      status[s.id] = s.data.complete === true;
    });
    return status;
  }, [sections]);

  if (sections.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">No data available</p>
        <p className="text-sm mt-2">Complete the form to see information here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {headerContent}

      <div className="flex gap-6">
        <ReviewSidebar
          title={sidebarTitle}
          sections={sidebarSections}
          completionStatus={completionStatus}
          activeSection={activeSection}
          onSectionClick={scrollToSection}
        />

        <div className="flex-1 min-w-0">
          <ReviewHeader
            title={title}
            subtitle={subtitle}
            completionPercentage={completionPercentage}
            status={status}
            lastUpdated={lastUpdated}
            onEdit={onEdit}
            onDownloadPDF={onDownloadPDF}
            actions={actions}
          />

          <div className="space-y-4 mt-6">
            {sections.map((section) => (
              <SectionCard
                key={section.id}
                id={section.id}
                title={section.title}
                icon={section.icon}
                data={section.data}
              />
            ))}
          </div>

          {footerContent}
        </div>
      </div>
    </div>
  );
};

export default ReviewLayout;
